import type { JobStatusResponse } from '../../types'
import Spinner from '../ui/Spinner'

interface JobProgressProps {
  status: JobStatusResponse
}

export default function JobProgress({ status }: JobProgressProps) {
  const getStatusColor = () => {
    switch (status.status) {
      case 'completed':
        return 'text-green-600'
      case 'failed':
        return 'text-red-600'
      case 'cancelled':
        return 'text-gray-600'
      default:
        return 'text-blue-600'
    }
  }

  const hasUnresolved = (status.unresolved_count ?? 0) > 0

  return (
    <div className="space-y-4 p-6 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Job Status</h3>
        <span className={`font-medium ${getStatusColor()}`}>
          {status.status.toUpperCase()}
        </span>
      </div>

      {status.status === 'processing' && (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Spinner size="sm" />
            <span className="text-sm text-gray-600">
              {status.progress_message || 'Processing...'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${status.progress}%` }}
            />
          </div>
          <div className="text-right text-sm text-gray-600">
            {status.progress}%
          </div>
        </div>
      )}

      {status.status === 'completed' && status.result_path && (
        <div className="text-green-600">
          ✓ PDF generated successfully!
        </div>
      )}

      {hasUnresolved && status.unresolved_cards && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
            <span>⚠️</span>
            <span>
              {status.unresolved_count} carta{status.unresolved_count !== 1 ? 's' : ''} no encontrada{status.unresolved_count !== 1 ? 's' : ''} en la base de datos
            </span>
          </div>
          <ul className="space-y-1">
            {status.unresolved_cards.map((card, idx) => (
              <li key={idx} className="text-sm text-red-600 flex items-start gap-2">
                <span className="text-red-400 mt-0.5">•</span>
                <span>
                  <span className="font-medium">{card.name}</span>
                  <span className="text-red-500 ml-1">({card.reason})</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {status.status === 'failed' && status.error && (
        <div className="text-red-600">
          ✗ Error: {status.error}
        </div>
      )}

      {status.status === 'cancelled' && (
        <div className="text-gray-600">
          Job was cancelled
        </div>
      )}
    </div>
  )
}
